import React, { useState, useEffect } from "react";
import { Alert, Anchor, Box, Button, PasswordInput, Popover, Progress, Stack, Text, TextInput } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { useForm } from "@mantine/form";
import { verifyCodeAndResetPassword } from "../../api/Auth";

const requirements = [
    {re: /[0-9]/, label: 'Includes number'},
    {re: /[a-z]/, label: 'Includes lowercase letter'},
    {re: /[A-Z]/, label: 'Includes uppercase letter'},
];

function PasswordRequirement({meets, label}: { meets: boolean; label: string }) {
    return (
        <Text
            color={meets ? 'teal' : 'red'}
            style={{display: 'flex', alignItems: 'center'}}
            mt={7}
            size="sm"
        >
            {meets ? <IconCheck style={{width: 14, height: 14}}/> : <IconX style={{width: 14, height: 14}}/>} <Box
            ml={10}>{label}</Box>
        </Text>
    );
}

function getStrength(password: string) {
    let multiplier = password.length > 7 ? 0 : 1;

    requirements.forEach((requirement) => {
        if (!requirement.re.test(password)) {
            multiplier += 1;
        }
    });

    return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}


// todo: it would be ideal if the password reset was username and not email?
export function ResetForgottenPassword() {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [popoverOpened, setPopoverOpened] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');

    const form = useForm({
        initialValues: {
            code: '',
            newPassword: '',
            confirmPassword: '',
        },
        validate: {
            code: (val) => (val.length === 6 ? null : 'Verification code must be 6 characters'),
            newPassword: (val) => {
                if (val.length < 8) return 'Password must be at least 8 characters';
                if (!requirements.every(req => req.re.test(val))) {
                    return 'Password must include number, lowercase and uppercase letter';
                }
                return null;
            },
            confirmPassword: (val, values) => (val === values.newPassword ? null : 'Passwords do not match'),
        },
    });

    console.log("hello from reset forgotten password");
    console.log(searchParams);
    if (!email) {
        console.log("no email found?");
        navigate('/forgot-password');
        return null;
    }

    const handleResetPassword = async (values: { code: string; newPassword: string }) => {
        setSubmitting(true);
        try {
            const result = await verifyCodeAndResetPassword(
                email,
                values.code,
                values.newPassword
            );
            if (result.success) {
                navigate('/login?passwordReset=true');
            } else {
                setError(result.error || 'Failed to reset password');
            }
        } catch (error) {
            setError('An unexpected error occurred');
        } finally {
            setSubmitting(false);
        }
    };

    const checks = requirements.map((requirement, index) => (
        <PasswordRequirement
            key={index}
            label={requirement.label}
            meets={requirement.re.test(form.values.newPassword)}
        />
    ));

    const strength = getStrength(form.values.newPassword);
    const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

    return (
        <>
            <Text size="xl" fw={500} mb="md">
                Enter Verification Code
            </Text>
            <Text size="sm" mb="md" c="dimmed">
                We've sent a verification code to <b>{email}</b>.  Enter it below to reset your password.
            </Text>
            {error && (
                <Alert
                    title="Error"
                    color="red"
                    mb="lg"
                    onClose={() => setError(null)}
                    withCloseButton
                >
                    {error}
                </Alert>
            )}

            <form onSubmit={form.onSubmit(handleResetPassword)}>
                <Stack>
                    <TextInput
                        required
                        label="Verification Code"
                        description="Enter the 6-digit verification code from your email:"
                        placeholder="123456"
                        value={form.values.code}
                        onChange={(event) => form.setFieldValue('code', event.currentTarget.value)}
                        error={form.errors.code}
                        radius="md"
                    />

                    <Popover opened={popoverOpened} position="bottom" width="target"
                             transitionProps={{transition: 'pop'}}>
                        <Popover.Target>
                            <div
                                onFocusCapture={() => setPopoverOpened(true)}
                                onBlurCapture={() => setPopoverOpened(false)}
                            >
                                <PasswordInput
                                    required
                                    label="New Password"
                                    placeholder="Your new password"
                                    value={form.values.newPassword}
                                    onChange={(event) => form.setFieldValue('newPassword', event.currentTarget.value)}
                                    error={form.errors.newPassword}
                                    radius="md"
                                />
                            </div>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Progress color={color} value={strength} size={5} mb="xs"/>
                            <PasswordRequirement label="Includes at least 8 characters"
                                                 meets={form.values.newPassword.length >= 8}/>
                            {checks}
                        </Popover.Dropdown>
                    </Popover>

                    <PasswordInput
                        required
                        label="Confirm New Password"
                        placeholder="Confirm your new password"
                        value={form.values.confirmPassword}
                        onChange={(event) => form.setFieldValue('confirmPassword', event.currentTarget.value)}
                        error={form.errors.confirmPassword}
                        radius="md"
                    />

                    <Button
                        mt='md'
                        type="submit"
                        radius="xl"
                        color="blue"
                        loading={submitting}
                        fullWidth
                    >
                        Reset Password
                    </Button>

                    <Text ta='center' size="sm" mt="sm" c="dimmed">
                        Not seeing it? Check your spam folder. If you still can't find it,{' '}
                        <Anchor component="button" type="button" onClick={() => navigate('/forgot-password')} c="blue">
                            try a different email
                        </Anchor>
                        {' '}or{' '}
                        <Anchor component="button" type="button" onClick={() => navigate('/signup')} c="blue">
                            create an account
                        </Anchor>.
                    </Text>
                </Stack>
            </form>
        </>
    );
}